import React, { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink,
  BreadcrumbSeparator, BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Loader2,
  ArrowLeft, Users, Mail,
} from 'lucide-react';
import {
  adminImportPlayerPreview, adminImportPlayerConfirm, friendlyError,
} from '@/lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  valid: {
    label: 'Válida',
    className: 'bg-emerald-100 text-emerald-800',
    icon: CheckCircle2,
  },
  duplicate: {
    label: 'Posible duplicado',
    className: 'bg-amber-100 text-amber-800',
    icon: Copy,
  },
  error: {
    label: 'Error',
    className: 'bg-[var(--ns-red)]/15 text-[var(--ns-red)]',
    icon: AlertTriangle,
  },
};

export default function AdminPlayerProfilesImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef(null);

  const summary = preview?.summary || null;
  const rows = preview?.rows || [];

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Solo se admiten ficheros .xlsx');
      return;
    }
    setFile(f);
    setPreview(null);
    setDone(null);
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminImportPlayerPreview(file);
      setPreview(data);
      toast.success(
        `Vista previa generada: ${data?.summary?.valid || 0} válidas, ${
          data?.summary?.errors || 0
        } errores, ${data?.summary?.duplicates || 0} duplicados`,
      );
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setUploading(false);
    }
  };

  const onConfirm = async () => {
    if (!preview?.preview_id || confirming) return;
    setConfirming(true);
    try {
      const data = await adminImportPlayerConfirm(preview.preview_id);
      setDone(data);
      toast.success(
        data?.already_confirmed
          ? 'La importación ya estaba confirmada.'
          : `Importación confirmada: ${data?.summary?.created || 0} fichas creadas.`,
      );
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setConfirming(false);
      setShowConfirm(false);
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/admin">Admin</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/admin/fichas-jugadores">Fichas de jugador</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Importar Excel</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl text-[var(--ns-text)]">
              Importar fichas de jugador
            </h1>
            <p className="text-sm text-[var(--ns-text-muted)] mt-1 max-w-2xl">
              Sube un fichero <strong>.xlsx</strong> con las columnas{' '}
              <em>Nombre, Primer apellido, Segundo apellido, Club, Email</em>.
              Máximo 500 filas. Las fichas se crean como <strong>borrador</strong>{' '}
              y nunca se vinculan automáticamente a usuarios existentes.
            </p>
          </div>
          <Button
            variant="outline"
            className="h-9 rounded-xl"
            onClick={() => navigate('/admin/fichas-jugadores')}
            data-testid="admin-import-back"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
        </div>

        {/* Paso 1: Subir fichero */}
        <Card className="p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="admin-import-upload-card">
          <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)]">
            <FileSpreadsheet className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[var(--ns-text)]">
              {file ? file.name : 'Selecciona un .xlsx'}
            </p>
            <p className="text-xs text-[var(--ns-text-muted)]">
              Formato aceptado: .xlsx (sin macros). Tamaño máximo 2 MiB.
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onPickFile}
            className="hidden"
            data-testid="admin-import-file-input"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-10 rounded-xl"
              onClick={() => inputRef.current?.click()}
              data-testid="admin-import-pick-file"
            >
              <Upload className="h-4 w-4 mr-1" /> Elegir archivo
            </Button>
            <Button
              className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0 disabled:opacity-60"
              disabled={!file || uploading}
              onClick={onUpload}
              data-testid="admin-import-validate"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Validando…</>
              ) : (
                'Validar'
              )}
            </Button>
          </div>
        </Card>

        {/* Paso 2: Preview */}
        {preview ? (
          <Card className="p-5 rounded-2xl" data-testid="admin-import-preview-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-[var(--ns-text)]">
                  Vista previa — {summary?.total || 0} filas
                </p>
                <p className="text-xs text-[var(--ns-text-muted)]">
                  Solo se crearán fichas al confirmar. Las filas con error o
                  posibles duplicados <strong>no</strong> se importan.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  {summary?.valid || 0} válidas
                </Badge>
                <Badge className="bg-amber-100 text-amber-800">
                  <Copy className="h-3 w-3 mr-1" />
                  {summary?.duplicates || 0} duplicados
                </Badge>
                <Badge className="bg-[var(--ns-red)]/15 text-[var(--ns-red)]">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {summary?.errors || 0} errores
                </Badge>
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-import-rows-table">
                <thead className="text-xs uppercase text-[var(--ns-text-muted)] border-b border-[var(--ns-border)]">
                  <tr>
                    <th className="text-left py-2 pr-3 w-10">#</th>
                    <th className="text-left py-2 pr-3">Jugador</th>
                    <th className="text-left py-2 pr-3">Club</th>
                    <th className="text-left py-2 pr-3">Categoría</th>
                    <th className="text-left py-2 pr-3">Email</th>
                    <th className="text-left py-2 pr-3">Estado</th>
                    <th className="text-left py-2 pr-3">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.valid;
                    const Icon = meta.icon;
                    const reason =
                      (r.errors && r.errors.length > 0 && r.errors.join(' · ')) ||
                      (r.dup_in_file ? 'Duplicado dentro del archivo' : '') ||
                      (r.dup_in_db ? `Posible duplicado con ficha existente` : '') ||
                      (r.warnings && r.warnings.length > 0 ? r.warnings.join(' · ') : '') ||
                      '—';
                    return (
                      <tr
                        key={r.row_number}
                        className="border-b border-[var(--ns-border)]/60"
                        data-testid={`admin-import-row-${r.row_number}`}
                      >
                        <td className="py-2 pr-3 text-[var(--ns-text-muted)]">{r.row_number}</td>
                        <td className="py-2 pr-3">
                          <div className="font-medium text-[var(--ns-text)]">
                            {`${r.first_name || ''} ${r.first_surname || ''} ${r.second_surname || ''}`.trim() || '—'}
                          </div>
                          {r.normalized_full_name ? (
                            <div className="text-[10px] font-mono text-[var(--ns-text-subtle)]">
                              {r.normalized_full_name}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          {r.club_name ? (
                            <span className="text-[var(--ns-text)]">{r.club_name}</span>
                          ) : (
                            <span className="italic text-[var(--ns-text-muted)]">
                              {r.club_input || '—'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3" data-testid={`admin-import-row-category-${r.row_number}`}>
                          {r.team_name || r.team_category ? (
                            <span className="text-[var(--ns-text)] capitalize">
                              {r.team_category || r.team_name}
                            </span>
                          ) : r.category_input ? (
                            <span className="italic text-[var(--ns-text-muted)]">
                              {r.category_input}
                            </span>
                          ) : (
                            <span className="text-[var(--ns-text-muted)]">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-[var(--ns-text-muted)]">{r.email || '—'}</td>
                        <td className="py-2 pr-3">
                          <Badge className={`text-[10px] uppercase ${meta.className}`}>
                            <Icon className="h-3 w-3 mr-1" />
                            {meta.label}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-xs text-[var(--ns-text-muted)]">{reason}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-xl"
                onClick={() => { setPreview(null); setFile(null); }}
                data-testid="admin-import-reset"
                disabled={confirming}
              >
                Descartar
              </Button>
              <Button
                className="h-10 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0 disabled:opacity-60"
                onClick={() => setShowConfirm(true)}
                disabled={!summary?.valid || confirming || !!done}
                data-testid="admin-import-confirm"
              >
                {confirming ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Importando…</>
                ) : (
                  <>Confirmar importación ({summary?.valid || 0})</>
                )}
              </Button>
            </div>
          </Card>
        ) : null}

        {done ? (
          <Card className="p-5 rounded-2xl flex flex-col gap-3" data-testid="admin-import-done-card">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--ns-text)]">
                  Importación completada
                </p>
                <p className="text-xs text-[var(--ns-text-muted)]">
                  Batch: <code className="text-[10px]">{done.import_batch_id}</code>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1 text-[var(--ns-text)]">
                <Users className="h-4 w-4" /> {done?.summary?.created || 0} fichas creadas
              </span>
              <span className="inline-flex items-center gap-1 text-[var(--ns-text)]">
                <Mail className="h-4 w-4" /> {done?.summary?.invitations_sent || 0} invitaciones enviadas
              </span>
              {done?.summary?.create_errors ? (
                <span className="inline-flex items-center gap-1 text-[var(--ns-red)]">
                  <AlertTriangle className="h-4 w-4" /> {done.summary.create_errors} errores al crear
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button
                asChild
                className="h-9 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold border-0"
              >
                <Link to="/admin/fichas-jugadores" data-testid="admin-import-go-list">
                  Volver a Fichas
                </Link>
              </Button>
            </div>
          </Card>
        ) : null}
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent data-testid="admin-import-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar importación</AlertDialogTitle>
            <AlertDialogDescription>
              Se crearán {summary?.valid || 0} fichas como <strong>borrador</strong>{' '}
              y se enviarán invitaciones a los emails válidos no asociados a
              usuarios existentes. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirming}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirm}
              disabled={confirming}
              data-testid="admin-import-confirm-accept"
            >
              {confirming ? 'Importando…' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
